import React, { ErrorInfo, ReactNode } from 'react';
import { Platform, View, Text, TouchableOpacity } from 'react-native';
import { storage } from '../services/storage';
import { makeStyles, themed } from '../theme';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Static fallback UI when error occurs */
  fallback?: ReactNode;
  /** Render fallback with retry/error (e.g. for native so fallback can call retry) */
  fallbackRender?: (retry: () => void, error: Error | null, retryCount: number) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  retryCount: number;
  lastPropsSignature: string;
}

/**
 * 🛡️ PHASE 2 FIX: Error Boundary with Existing Infrastructure
 * 
 * Prevents infinite render loops and provides graceful error recovery.
 * Uses feature flag for easy rollback if needed.
 * 
 * Features:
 * - Automatic retry with exponential backoff
 * - Props change detection for auto-reset
 * - Detailed error logging for debugging
 * - Graceful fallback UI
 * - Memory leak prevention
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeouts: Set<ReturnType<typeof setTimeout>> = new Set();
  
  // 🚨 FEATURE FLAG: Enable enhanced error boundaries
  private static ENABLE_ERROR_BOUNDARIES = true;
  
  constructor(props: ErrorBoundaryProps) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      retryCount: 0,
      lastPropsSignature: this.getPropsSignature(props)
    };
  }
  
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    if (!ErrorBoundary.ENABLE_ERROR_BOUNDARIES) {
      // Feature flag disabled - let error propagate
      throw error;
    }
    
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }
  
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 ErrorBoundary caught error:', {
      error: error.message,
      stack: error.stack,
      errorInfo,
      errorId: this.state.errorId,
      retryCount: this.state.retryCount
    });
    
    this.setState({ errorInfo });
    
    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    // 🛡️ RENDER LOOP PREVENTION: Auto-retry with exponential backoff
    this.scheduleRetry();
  }
  
  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // 🔄 AUTO-RESET: Reset error state when props change
    if (this.props.resetOnPropsChange) {
      const currentSignature = this.getPropsSignature(this.props);
      if (currentSignature !== this.state.lastPropsSignature) {
        console.log('🔄 ErrorBoundary: Props changed, resetting error state');
        this.resetErrorBoundary();
        this.setState({ lastPropsSignature: currentSignature });
      }
    }
    
    // Reset on resetKeys change
    if (this.props.resetKeys && prevProps.resetKeys) {
      const keysChanged = this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys![index]
      );
      if (keysChanged) {
        console.log('🔄 ErrorBoundary: Reset keys changed, resetting error state');
        this.resetErrorBoundary();
      }
    }
  }
  
  componentWillUnmount() {
    // 🧹 CLEANUP: Clear all timeouts to prevent memory leaks
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
    this.retryTimeouts.clear();
  }
  
  private getPropsSignature(props: ErrorBoundaryProps): string {
    // Create a signature of props for change detection
    return JSON.stringify({
      resetKeys: props.resetKeys,
      childrenType: typeof props.children
    });
  }
  
  // 🔧 M35B FIX: Clear corrupt chat message cache from AsyncStorage.
  // When a crash is caused by corrupt persisted data (e.g., message.text = undefined),
  // retrying without clearing the cache will always fail. This nuclear recovery
  // clears the cache so the next retry loads fresh data from the backend.
  private clearCorruptCache = async () => {
    try {
      const userId = await storage.getItem('user_id');
      if (userId) {
        await storage.removeItem(`${userId}_chat_messages`);
        console.log('🧹 ErrorBoundary: Cleared corrupt chat message cache for recovery');
      }
    } catch (e) {
      console.warn('⚠️ ErrorBoundary: Failed to clear cache:', e);
    }
  };

  private scheduleRetry = () => {
    const { retryCount } = this.state;

    // 🛑 TERMINAL: nuclear recovery already ran (retryCount was set to 999)
    // and the error recurred — the error is deterministic. Stay on the
    // fallback UI (manual retry remains available). Without this bail-out,
    // 999 >= 3 re-entered the nuclear branch below on every recurrence:
    // clear cache → 1s → remount → throw → repeat, forever — an infinite
    // remount/fetch loop that presented as a hard app crash on the Health
    // tab (build 234) instead of the fallback card.
    if (retryCount >= 999) {
      console.warn('🛑 ErrorBoundary: Error persists after nuclear recovery — staying on fallback UI');
      return;
    }

    // 🚨 RENDER LOOP PREVENTION: Max 3 retries to prevent infinite loops
    if (retryCount >= 3) {
      console.warn('🛑 ErrorBoundary: Max retries reached — clearing corrupt cache for final recovery attempt');
      // 🔧 M35B FIX: On max retries, clear potentially corrupt cached messages
      // then allow one final retry with clean state. This prevents the
      // "reinstall required" scenario where corrupt data in AsyncStorage
      // causes a permanent crash loop.
      this.clearCorruptCache().then(() => {
        const timeout = setTimeout(() => {
          this.retryTimeouts.delete(timeout);
          this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: 999, // Prevent further retries after nuclear recovery
          });
        }, 1000);
        this.retryTimeouts.add(timeout);
      });
      return;
    }
    
    // Exponential backoff: 1s, 2s, 4s
    const delay = Math.pow(2, retryCount) * 1000;
    
    console.log(`🔄 ErrorBoundary: Scheduling retry ${retryCount + 1} in ${delay}ms`);
    
    const timeout = setTimeout(() => {
      this.retryTimeouts.delete(timeout);
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
    }, delay);
    
    this.retryTimeouts.add(timeout);
  };
  
  private resetErrorBoundary = () => {
    // Clear all retry timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
    this.retryTimeouts.clear();
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    });
  };
  
  private handleManualRetry = () => {
    console.log('🔄 ErrorBoundary: Manual retry triggered');
    this.resetErrorBoundary();
  };
  
  render() {
    if (!ErrorBoundary.ENABLE_ERROR_BOUNDARIES) {
      return this.props.children;
    }
    
    if (this.state.hasError) {
      // Render prop fallback (e.g. native so retry can be passed)
      if (typeof this.props.fallbackRender === 'function') {
        return this.props.fallbackRender(
          this.handleManualRetry,
          this.state.error,
          this.state.retryCount
        );
      }
      // Static custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // 🔧 FIX (Mar 18, 2026): Platform-safe default fallback
      // Old fallback used raw HTML (<div>, <button>, window.location.reload)
      // which would crash on native. Now uses React Native primitives.
      return (
        <ErrorBoundaryFallback
          retryCount={this.state.retryCount}
          onRetry={this.handleManualRetry}
          onClearAndRetry={() => {
            console.log('🧹 ErrorBoundary: Manual cache clear + retry');
            this.clearCorruptCache().then(() => this.resetErrorBoundary());
          }}
        />
      );
    }
    
    return this.props.children;
  }
}

function ErrorBoundaryFallback({ retryCount, onRetry, onClearAndRetry }: { retryCount: number; onRetry: () => void; onClearAndRetry: () => void }) {
  const styles = useStyles();
  return (
    <View style={styles.box}>
      <Text style={styles.heading}>
        ⚠️ Something went wrong
      </Text>
      <Text style={styles.line}>
        I encountered an error while processing your request. Don't worry — I'm working on it!
      </Text>
      {retryCount < 3 && (
        <Text style={styles.retryLine}>
          🔄 Automatic retry {retryCount + 1}/3 in progress...
        </Text>
      )}
      <View style={styles.buttons}>
        <TouchableOpacity onPress={onRetry} style={[styles.button, styles.tryAgain]}>
          <Text style={styles.tryAgainText}>Try Again</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onClearAndRetry} style={[styles.button, styles.clearCache]}>
          <Text style={styles.clearCacheText}>Clear Cache & Retry</Text>
        </TouchableOpacity>
        {Platform.OS === 'web' && (
          <TouchableOpacity onPress={() => window.location.reload()} style={[styles.button, styles.refresh]}>
            <Text style={styles.refreshText}>Refresh Page</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const useStyles = makeStyles((t) => ({
  box: { padding: 20, margin: 10, borderRadius: 8, backgroundColor: themed(t, '#ffebee', t.status.errorTint) },
  heading: { fontSize: 16, fontWeight: '600', color: themed(t, '#c62828', t.status.errorDeep), marginBottom: 8 },
  line: { fontSize: 14, color: themed(t, '#666', t.ink.secondary), marginBottom: 12 },
  retryLine: { fontSize: 12, color: themed(t, '#888', t.ink.tertiary), marginBottom: 12 },
  buttons: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  button: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 4 },
  tryAgain: { backgroundColor: themed(t, '#4caf50', t.fill.brand) },
  tryAgainText: { color: t.ink.onFill, fontSize: 14 },
  clearCache: { backgroundColor: themed(t, '#ff9800', t.status.warning) },
  clearCacheText: { color: themed(t, t.ink.onFill, t.ink.onBright), fontSize: 14 },
  refresh: { backgroundColor: themed(t, '#2196f3', t.status.info) },
  refreshText: { color: themed(t, t.ink.onFill, t.ink.onBright), fontSize: 14 },
}));

// Convenience wrapper for functional components
export const withErrorBoundary = <T extends {}>(
  Component: React.ComponentType<T>
) => {
  const WrappedComponent = (props: T) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default ErrorBoundary; 